import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { UserType } from "@core/enums/user-type";
import { ServiceHelper } from "@core/helpers/service-helper";
import { AccountHistoricValue } from "@core/models/account-historic-values.model";
import { AccountRiskProfile } from "@core/models/account-risk-profile.model";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { Configuration } from "@core/models/configuration.model";
import {
  DisbursementWithTax,
  TDisbursementsWithTaxPaginated,
} from "@core/models/disbursement-with-tax.model";
import { AccountFund } from "@core/models/fund.model";
import { FundRatioHistory } from "@core/models/fund-ratio-history.model";
import { GeneralAccountCorrection } from "@core/models/general-account-correction.model";
import {
  GroupedGfhTransaction,
  TGroupedGfhTransactionsPaginated,
} from "@core/models/grouped-gfh-transaction.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TAllClients, TAllClientsPaginated } from "@core/models/user.model";
import { CompanyUser } from "@core/models/user-company.model";
import { TPagination } from "@custom-types/sorting";
import { CrudType } from "@enums/crud-type";
import { LetterType } from "@enums/letter-type";
import { environment } from "@env/environment";
import { ExtendedGfhAccount } from "@models/extended-gfh-account.model";
import { GfhAccountClosure } from "@models/gfh-account-closure.model";
import { InternalTransfer } from "@models/internal-transfer.model";
import { Quotation } from "@models/quotation.model";
import { PrivateUser } from "@models/user-private.model";
import { WithdrawalAssignment } from "@models/withdrawal-assignment.model";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ClientsAndAccountsService {
  constructor(private http: HttpClient) {}

  getClients$(
    searchTerm?: string,
    pagination?: Partial<TPagination>,
    userType?: UserType
  ): Observable<Array<TAllClients> | TAllClientsPaginated> {
    let params = ServiceHelper.getListQueryParams(searchTerm, pagination);
    if (userType !== undefined) {
      params = params.set("individual", userType);
    }
    return this.http
      .get<
        Array<TAllClients> | TAllClientsPaginated
      >(`${environment.BASE_URL}client/`, { params })
      .pipe(
        map(
          (
            response: Array<TAllClients> | TAllClientsPaginated
          ): Array<TAllClients> | TAllClientsPaginated => {
            return response;
          }
        )
      );
  }

  getClientAccountList$(id?: string): Observable<Array<ExtendedGfhAccount>> {
    return this.http
      .get<
        Array<ExtendedGfhAccount>
      >(`${environment.BASE_URL}client/${id}/gfhaccountportfolio/`)
      .pipe(
        map(
          (response: Array<ExtendedGfhAccount>): Array<ExtendedGfhAccount> => {
            return response;
          }
        )
      );
  }

  saveClient$(client: Partial<PrivateUser | CompanyUser>, id?: string) {
    const create = id === "";

    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}client/${!create ? `${id}` : ""}`,
      client
    );
  }

  checkClientBsn$(clientBsn: string, countryCode: string): Observable<boolean> {
    let params = new HttpParams({});
    if (clientBsn) {
      params = params.append("clientBsn", clientBsn);
    }
    if (countryCode) {
      params = params.append("countryCode", countryCode);
    }
    return this.http
      .get<boolean>(`${environment.BASE_URL}client/validateBSN`, { params })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }

  recoverPassword$(clientId: string): Observable<unknown> {
    return this.http.patch(`${environment.BASE_URL}client/${clientId}`, {});
  }

  sendLetter$(
    clientId: string,
    generalLetter: boolean,
    sendToCustomer: boolean
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}client/${clientId}/${generalLetter ? LetterType.General : LetterType.Modification}`,
      generalLetter ? {} : sendToCustomer
    );
  }

  saveAccount$(
    account: Partial<ExtendedGfhAccount>,
    id: string | undefined
  ): Observable<unknown> {
    const create = id === "";
    return this.http[create ? CrudType.Post : CrudType.Put](
      `${environment.BASE_URL}gfhaccount/${!create ? `${id}` : ""}`,
      account
    );
  }

  getAccountDetails$(id: string): Observable<ExtendedGfhAccount> {
    return this.http
      .get<ExtendedGfhAccount>(`${environment.BASE_URL}gfhaccount/${id}`)
      .pipe(
        map((response: ExtendedGfhAccount): ExtendedGfhAccount => {
          return response;
        })
      );
  }

  getAccount$(accountNumber: string): Observable<ExtendedGfhAccount> {
    const params = new HttpParams().set("accountNumber", accountNumber);
    return this.http
      .get<ExtendedGfhAccount>(`${environment.BASE_URL}gfhaccount/number`, {
        params,
      })
      .pipe(
        map((response: ExtendedGfhAccount): ExtendedGfhAccount => {
          return response;
        })
      );
  }

  getTransactionsForAccount$(
    id: string,
    pagination?: Partial<TPagination>
  ): Observable<
    Array<GroupedGfhTransaction> | TGroupedGfhTransactionsPaginated
  > {
    const params = ServiceHelper.getListQueryParams(undefined, pagination);
    return this.http
      .get<
        Array<GroupedGfhTransaction> | TGroupedGfhTransactionsPaginated
      >(`${environment.BASE_URL}gfhaccount/${id}/gfhtransaction`, { params })
      .pipe(
        map(
          (
            response:
              | Array<GroupedGfhTransaction>
              | TGroupedGfhTransactionsPaginated
          ):
            | Array<GroupedGfhTransaction>
            | TGroupedGfhTransactionsPaginated => {
            return response;
          }
        )
      );
  }

  generalAccountCorrectionList$(
    id: string
  ): Observable<Array<GeneralAccountCorrection>> {
    return this.http
      .get<
        Array<GeneralAccountCorrection>
      >(`${environment.BASE_URL}gfhaccount/${id}/generalaccountcorrection`)
      .pipe(
        map(
          (
            response: Array<GeneralAccountCorrection>
          ): Array<GeneralAccountCorrection> => {
            return response;
          }
        )
      );
  }

  createCorrection$(
    correction: Partial<GeneralAccountCorrection>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}generalaccountcorrection/`,
      correction
    );
  }

  exportTransactions$(exportInformation: {
    gfhAccountId: string;
    startDate: string;
    endDate: string;
  }): Observable<unknown> {
    const params = new HttpParams()
      .set("gfhAccountId", exportInformation.gfhAccountId)
      .set("from", exportInformation.startDate)
      .set("untill", exportInformation.endDate);
    return this.http.get(
      `${environment.BASE_URL}report/transactionoverviewreport`,
      { params }
    );
  }

  getAccountHistoricValueList$(
    accountId: string,
    start: number,
    take: number
  ): Observable<Array<AccountHistoricValue>> {
    const params = new HttpParams()
      .set("accountId", accountId)
      .set("start", start)
      .set("take", take);
    return this.http
      .get<
        Array<AccountHistoricValue>
      >(`${environment.BASE_URL}gfhaccount/accountHistoricValues/`, { params })
      .pipe(
        map(
          (
            response: Array<AccountHistoricValue>
          ): Array<AccountHistoricValue> => {
            return response;
          }
        )
      );
  }

  getAccountConfiguration$(): Observable<Configuration> {
    return this.http
      .get<Configuration>(`${environment.BASE_URL}modzconfig/switches`)
      .pipe(
        map((response: Configuration): Configuration => {
          return response;
        })
      );
  }

  getWithdrawalAssignmentList$(
    id: string
  ): Observable<Array<WithdrawalAssignment>> {
    return this.http
      .get<
        Array<WithdrawalAssignment>
      >(`${environment.BASE_URL}gfhaccount/${id}/withdrawalassignment`)
      .pipe(
        map(
          (
            response: Array<WithdrawalAssignment>
          ): Array<WithdrawalAssignment> => {
            return response;
          }
        )
      );
  }

  deleteWithdrawalAssignment$(id: string): Observable<unknown> {
    return this.http.delete(
      `${environment.BASE_URL}withdrawalassignment/${id}`
    );
  }

  getTradeForAccount$(id: string): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}trading/${id}/tradeForAccount`,
      {}
    );
  }

  saveWithdrawalAssignment$(
    payload: Partial<WithdrawalAssignment>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}withdrawalassignment`,
      payload
    );
  }

  updateAccountTransaction$(updateInformation: {
    id: string;
    description?: string;
    exchangeInformation?: boolean;
    version: string;
  }): Observable<unknown> {
    if (updateInformation.description !== undefined) {
      return this.http.patch(
        `${environment.BASE_URL}gfhtransaction/${updateInformation.id}/description`,
        {
          description: updateInformation.description,
          version: updateInformation.version,
        }
      );
    }
    return this.http.patch(
      `${environment.BASE_URL}gfhtransaction/${updateInformation.id}/exchangeinformation`,
      {
        exchangeInformation: updateInformation.exchangeInformation,
        version: updateInformation.version,
      }
    );
  }

  getAccountDisbursements$(
    id: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<DisbursementWithTax> | TDisbursementsWithTaxPaginated> {
    let params = ServiceHelper.getListQueryParams(undefined, pagination);
    params = params.set("startFrom", new Date().toISOString());

    return this.http
      .get<
        | {
            disbursements: Array<DisbursementWithTax>;
          }
        | TDisbursementsWithTaxPaginated
      >(
        `${environment.BASE_URL}gfhaccount/${id}/gfhtransaction/disbursement/`,
        { params }
      )
      .pipe(
        map(
          (
            response:
              | {
                  disbursements: Array<DisbursementWithTax>;
                }
              | TDisbursementsWithTaxPaginated
          ): Array<DisbursementWithTax> | TDisbursementsWithTaxPaginated => {
            return "disbursements" in response
              ? response.disbursements
              : response;
          }
        )
      );
  }

  closeAccount$(
    id: string,
    accountPayload: Partial<GfhAccountClosure>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}gfhaccount/${id}/close`,
      accountPayload
    );
  }

  sendClosingLetter$(id: string): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}gfhaccount/${id}/closingletter`,
      {}
    );
  }

  getPauseReasonStatus$(): Observable<boolean> {
    return this.http
      .get<boolean>(`${environment.BASE_URL}modzconfig/reasonforpaused`)
      .pipe(
        map((needreason: boolean): boolean => {
          return needreason;
        })
      );
  }

  sendFrontPageLetter$(id: string): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}gfhaccount/${id}/frontpageletter`,
      undefined
    );
  }

  sendWelcomeLetter$(
    id: string,
    letter: { sendToClient: boolean }
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}gfhaccount/${id}/welcomeletter`,
      letter
    );
  }

  getInternalTransferList$(id: string): Observable<Array<InternalTransfer>> {
    return this.http
      .get<
        Array<InternalTransfer>
      >(`${environment.BASE_URL}gfhaccount/${id}/internaltransfer`)
      .pipe(
        map((response: Array<InternalTransfer>): Array<InternalTransfer> => {
          return response;
        })
      );
  }

  saveInternalTransfer$(
    internalTransfer: Partial<InternalTransfer>
  ): Observable<unknown> {
    return this.http.post(
      `${environment.BASE_URL}internaltransfer`,
      internalTransfer
    );
  }

  deleteInternalTransfer$(id: string): Observable<unknown> {
    return this.http.delete(
      `${environment.BASE_URL}internaltransfer/${id}/delete`
    );
  }

  getQuotationList$(
    searchTerm?: string,
    pagination?: Partial<TPagination>
  ): Observable<Array<Quotation>> {
    const params = ServiceHelper.getListQueryParams(searchTerm, pagination);

    return this.http
      .get<Array<Quotation>>(`${environment.BASE_URL}quotation/`, { params })
      .pipe(
        map(
          (
            response: Array<Quotation> | { data: Array<Quotation> }
          ): Array<Quotation> => {
            return Array.isArray(response) ? response : response.data;
          }
        )
      );
  }

  getRiskProfiles$(): Observable<Array<RiskProfile>> {
    return this.http
      .get<Array<RiskProfile>>(`${environment.BASE_URL}riskprofile`)
      .pipe(
        map((response: Array<RiskProfile>): Array<RiskProfile> => {
          return response;
        })
      );
  }

  deleteQuotation$(id: string): Observable<unknown> {
    return this.http.delete(`${environment.BASE_URL}quotation/${id}`);
  }

  getCompositionProductFunds$(
    id: string
  ): Observable<Array<CompositionVersion>> {
    return this.http
      .get<
        Array<Array<CompositionVersion>>
      >(`${environment.BASE_URL}composition/product/${id}`)
      .pipe(
        map(
          (
            response: Array<Array<CompositionVersion>>
          ): Array<CompositionVersion> => {
            return response.flat();
          }
        )
      );
  }

  getProductRiskProfiles$(
    id: string
  ): Observable<Array<CompositionRiskProfile>> {
    return this.http
      .get<
        Array<CompositionRiskProfile>
      >(`${environment.BASE_URL}composition/risks/${id}`)
      .pipe(
        map(
          (
            response: Array<CompositionRiskProfile>
          ): Array<CompositionRiskProfile> => {
            return response;
          }
        )
      );
  }

  getImpersonationAccountRequest$(id: string): Observable<string> {
    return this.http
      .get<string>(`${environment.BASE_URL}impersonation/${id}`)
      .pipe(
        map((response: string): string => {
          return response;
        })
      );
  }

  getAccountRiskProfiles$(id: string): Observable<Array<AccountRiskProfile>> {
    return this.http
      .get<
        Array<AccountRiskProfile>
      >(`${environment.BASE_URL}riskprofile/${id}/accountRiskProfiles`)
      .pipe(
        map(
          (response: Array<AccountRiskProfile>): Array<AccountRiskProfile> => {
            return response;
          }
        )
      );
  }

  getFundRationHistory$(id: string): Observable<Array<FundRatioHistory>> {
    return this.http
      .get<
        Array<FundRatioHistory>
      >(`${environment.BASE_URL}gfhaccount/${id}/fundratiohistory/`)
      .pipe(
        map((response: Array<FundRatioHistory>): Array<FundRatioHistory> => {
          return response;
        })
      );
  }

  reverseCancelAccount$(id: string): Observable<unknown> {
    return this.http.patch(
      `${environment.BASE_URL}gfhaccount/${id}/reverseclose`,
      undefined
    );
  }

  checkForExistingEmail$(email: string): Observable<boolean> {
    let params = new HttpParams({});
    params = params.append("email", email);
    return this.http
      .get<boolean>(`${environment.BASE_URL}gfhaccount/email`, { params })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }

  getAccountFunds$(id: string): Observable<Array<AccountFund>> {
    return this.http
      .get<{
        funds: Array<AccountFund>;
      }>(`${environment.BASE_URL}gfhaccount/${id}/funds`)
      .pipe(
        map((response: { funds: Array<AccountFund> }): Array<AccountFund> => {
          return response.funds;
        })
      );
  }

  checkForExistingLeiNumber$(leiNumber: string): Observable<boolean> {
    const params = new HttpParams({}).set("leiNumber", leiNumber);
    return this.http
      .get<boolean>(`${environment.BASE_URL}client/leiNumberExists`, {
        params,
      })
      .pipe(
        map((response: boolean): boolean => {
          return response;
        })
      );
  }
}
